import {createContext, useCallback, useContext, useEffect, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {supabase} from "../services/supabase";

const INACTIVITY_LIMIT = 15 * 60 * 1000; // 15 minutes
const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const inactivityTimer = useRef(null);

    const loginPath = process.env.REACT_APP_LOGIN_ROUTE;
    const adminDashboardPath = process.env.REACT_APP_ADMIN_ROUTE;

    const logout = useCallback(async (reason) => {
        await supabase.auth.signOut();
        setUser(null);
        setRole(null);
        clearTimeout(inactivityTimer.current);
        navigate(loginPath);
        if (reason) alert(reason);
    }, [navigate,loginPath]);

    // ✅ Stable reset function using useCallback
    const resetInactivityTimer = useCallback(() => {
        clearTimeout(inactivityTimer.current);
        inactivityTimer.current = setTimeout(() => {
            void logout("Session expired due to inactivity.");
        }, INACTIVITY_LIMIT);
    }, [logout]);



    // ✅ Updated dependency-safe function
    const handleActivity = useCallback(() => {
        if (user) resetInactivityTimer();
    }, [user, resetInactivityTimer]);

    // Listen for user activity
    useEffect(() => {
        const activityEvents = ["mousemove", "keydown", "click", "scroll"];
        activityEvents.forEach(event => window.addEventListener(event, handleActivity));
        return () => {
            activityEvents.forEach(event => window.removeEventListener(event, handleActivity));
            clearTimeout(inactivityTimer.current);
        };
    }, [handleActivity]);

    // Fetch current session and listen to auth changes
    useEffect(() => {
        const init = async () => {
            const { data } = await supabase.auth.getSession();
            const session = data?.session;
            if (session?.user) {
                setUser(session.user);
                setRole(session.user.user_metadata?.role || null);
                resetInactivityTimer();
            }
            setLoading(false);
        };
        init();

        const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
            if (session?.user) {
                setUser(session.user);
                setRole(session.user.user_metadata?.role || null);
                resetInactivityTimer();
            } else {
                setUser(null);
                setRole(null);
                clearTimeout(inactivityTimer.current);
            }
        });

        return () => {
            listener?.subscription?.unsubscribe();
        };
    }, [resetInactivityTimer]);
    // 🚪 Login handler
    const login = async (email, password) => {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;

        const role = data.user.user_metadata?.role;
        if (role === "admin") {
            setUser(data.user);
            setRole(role);
            navigate(adminDashboardPath);
            resetInactivityTimer();
        } else {
            throw new Error("You are not authorized as an admin.");
        }
    };



    return (
        <AuthContext.Provider value={{ user, role, login, logout, loading }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
